import { useMemo } from "react";

import { Box, HStack, Spacer, Stack, Text } from "@chakra-ui/react";

import { UserManagementModel } from "api";
import { DesktopOnly, MobileOnly } from "components/BreakpointVisibility";
import Empty from "components/Empty";
import SearchBoxWithIcon from "components/SearchBoxWithIcon";
import SortTermsSelect from "components/SortTermsSelect";
import { SortableCardTable } from "components/SortableTable";
import { useRequestParams } from "contexts/RequestParamsProvider";
import useFeatureFlag from "hooks/useFeatureFlag";
import { removeNullable } from "services/apiHelpers";
import { permissionValueToDisplayValue } from "services/customerPermissionsHelpers";
import { USER_MANAGEMENT } from "util/featureFlags";
import { EditUserModalButton } from "./EditUserPermissions/EditUserPermissionsModal";
import { InviteUserButton } from "./InviteNewUserModal/InviteNewUserModal";

export const USER_PENDING_STATUS = "Pending";

export const USER_ACTIVE_STATUS = "Active";

const getStatusDisplayName = (status: string | null | undefined) => {
  if (status === USER_PENDING_STATUS) {
    return "Pending";
  } else if (status === USER_ACTIVE_STATUS) {
    return "Active";
  } else {
    return "";
  }
};

const getStatusTextColor = (status: string | null | undefined) => {
  if (status === USER_PENDING_STATUS) {
    return "grey.500";
  } else if (status === USER_ACTIVE_STATUS) {
    return "brandGreen";
  } else {
    return "";
  }
};

const consolidateUsers = (users: UserManagementDetails[] = []) =>
  users?.filter(
    (currentUser, indexOfUser, arrayOfUser) =>
      arrayOfUser.findIndex((user) => user.userId === currentUser.userId) ===
      indexOfUser
  );

export const userManagementColumns: ColumnDefinition<UserManagementModel>[] = [
  {
    property: "firstName",
    sortKey: "FirstName",
    columnLabel: "First Name",
  },
  {
    property: "lastName",
    sortKey: "LastName",
    columnLabel: "Last Name",
  },
  {
    property: "phoneNumber",
    sortKey: "Phone",
    columnLabel: "Phone #",
  },
  {
    property: "email",
    sortKey: "Email",
    columnLabel: "Email Address",
    maxWidth: "85vw", // TODO: fix the table to support value maxW, this is maxW for label and value. 85vw was trial and error
  },
  {
    property: "permissions",
    columnLabel: "Permissions",
    maxWidth: "85vw", // TODO: fix the table to support value maxW, this is maxW for label and value. 85vw was trial and error
  },
  {
    property: "status",
    sortKey: "Status",
    columnLabel: "Status",
  },
];

const userManagementSortSelectOptions = removeNullable(
  userManagementColumns.map((column) =>
    column.sortKey && column.columnLabel
      ? {
          label: column.columnLabel,
          sortKey: column.sortKey,
        }
      : null
  )
);

const UserManagementTable = ({
  users,
}: {
  users: UserManagementDetails[] | undefined;
}) => {
  const { searchTerm, onChangeSearchTerm, activeSort, onChangeSort } =
    useRequestParams();
  const hasEditUserFlag = useFeatureFlag(USER_MANAGEMENT.EDIT);

  const userManagementTableEntries = useMemo(() => {
    const consolidatedUsers = consolidateUsers(users);

    return (
      consolidatedUsers?.map((value: UserManagementDetails) => {
        const displayTransformedPermissionName = value.permissions?.map(
          permissionValueToDisplayValue
        );

        return {
          ...value,
          status: (
            <Text color={getStatusTextColor(value.status)}>
              {getStatusDisplayName(value.status)}
            </Text>
          ),
          permissions:
            displayTransformedPermissionName?.join(", ") || DEFAULT_EMPTY_VALUE,
          edit: hasEditUserFlag ? <EditUserModalButton user={value} /> : "",
        };
      }) ?? []
    );
  }, [hasEditUserFlag, users]);

  return (
    <Stack>
      <DesktopOnly>
        <HStack
          spacing={4}
          direction="row"
          justifyContent="right"
          align="flex-end"
        >
          <Box>
            <SearchBoxWithIcon
              value={searchTerm}
              onChange={onChangeSearchTerm}
            />
          </Box>

          <Box style={{ marginTop: "18px" }}>
            <InviteUserButton />
          </Box>
        </HStack>
      </DesktopOnly>
      <MobileOnly>
        <Box w="100%">
          <SearchBoxWithIcon value={searchTerm} onChange={onChangeSearchTerm} />
        </Box>
        <HStack w="100%" align="flex-end" spacing={4}>
          <Box>
            <SortTermsSelect
              sortOptions={userManagementSortSelectOptions}
              selectedSortKey={activeSort?.key}
              selectedDirection={activeSort?.direction}
              onChange={onChangeSort}
            />
          </Box>
          <Spacer />
          <InviteUserButton />
        </HStack>
      </MobileOnly>
      {userManagementTableEntries && userManagementTableEntries.length > 0 ? (
        <SortableCardTable
          values={userManagementTableEntries}
          columns={[
            ...userManagementColumns,
            { property: "edit", columnLabel: "", hideTooltip: true },
          ]}
        />
      ) : (
        <Empty
          variant="search"
          title="No results found"
          maxWidth="400px"
          message="No users found"
        />
      )}
    </Stack>
  );
};

export default UserManagementTable;
